/**
 * The last few things that went wrong in this browser tab, for bug reports:
 * errors (console, uncaught, unhandled promises) and failed API requests.
 *
 * Only what helps reproduce a problem is kept: messages, the method and path
 * of a request (never its query or body, which can hold tokens and personal
 * data), its status and the API's request id.
 */

export interface CapturedError {
  message: string
  at: string
}

export interface CapturedRequest {
  method: string
  path: string
  status: number | null
  request_id: string | null
  at: string
}

const LIMIT = 15
const errors: CapturedError[] = []
const requests: CapturedRequest[] = []
let installed = false

function push<T>(list: T[], item: T) {
  list.push(item)
  if (list.length > LIMIT) list.shift()
}

function describe(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function now() {
  return new Date().toISOString()
}

/** Records an error from the console or an uncaught failure. */
export function recordError(...parts: unknown[]) {
  const message = parts.map(describe).join(' ').slice(0, 1_000)
  if (message) push(errors, { message, at: now() })
}

export function recordRequest(entry: Omit<CapturedRequest, 'at'>) {
  push(requests, { ...entry, at: now() })
}

/** Starts recording; safe to call more than once. */
export function installDiagnostics(apiBaseUrl: string) {
  if (installed || typeof window === 'undefined') return
  installed = true

  const originalError = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    recordError(...args)
    originalError(...args)
  }
  window.addEventListener('error', (event) => recordError(event.error ?? event.message))
  window.addEventListener('unhandledrejection', (event) => recordError('Unhandled:', event.reason))

  const apiOrigin = (() => {
    try {
      return new URL(apiBaseUrl, window.location.href).origin
    } catch {
      return null
    }
  })()
  const originalFetch = window.fetch.bind(window)
  const recordingFetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const url = new URL(
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
      window.location.href,
    )
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
    const isApi = url.origin === apiOrigin
    try {
      const response = await originalFetch(input, init)
      if (isApi && !response.ok) {
        let requestId: string | null = null
        try {
          const body = (await response.clone().json()) as { meta?: { requestId?: unknown } }
          requestId = typeof body.meta?.requestId === 'string' ? body.meta.requestId : null
        } catch {
          // Not JSON: no request id to record.
        }
        recordRequest({
          method,
          path: url.pathname,
          status: response.status,
          request_id: requestId,
        })
      }
      return response
    } catch (error) {
      if (isApi && !(error instanceof DOMException && error.name === 'AbortError')) {
        recordRequest({ method, path: url.pathname, status: null, request_id: null })
      }
      throw error
    }
  }
  window.fetch = Object.assign(recordingFetch, originalFetch) as typeof window.fetch
}

/** What a bug report sends along. */
export function diagnosticsSnapshot() {
  return {
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    console_errors: [...errors],
    failed_requests: [...requests],
  }
}
